// Unpublished Work © 2021-2026 Deere & Company.

import {Workbook} from 'exceljs';
import {autofitWorksheet, downloadExcelFile} from 'Utils/excel-utils';
import moment from 'moment';
import accounting from 'accounting';
import {sortBy} from 'lodash';
import {alphaNumericCompare, dateCompare} from 'Ui/models/maintenance';
import {
    capitalizeFirstLetter
} from 'Utils/translation-utils';
import {getHeaderColumnName} from 'Ui/features/onequip/reports/uptime-report/uptime-report-data-table';
import {EXPORT_FAIL} from 'Common/constants/errors';
import {translateErrorMessage} from '../utils/error-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {formatLaborReportWorksheetValues} from 'OnLabor/report/utils/labor-report-util';
import {
    getLaborByEmployeeExportContent,
    getLaborByStatusExportContent,
    getLaborWorkboardsExportContent,
    getWorkboardJobListExportContent,
    getEquipmentCostExportContent,
    getEquipmentUptimeExportContent,
    getLifetimeExportContent,
    getServiceHistoryExportContent
} from 'Utils/labor-report-export-utils';

const HOURS_COLUMN = 8;
const EST_HRS_COLUMN = 6;
const ACT_HRS_COLUMN = 7;

function showExportFailureToast(addToast, translations) {
    addToast({
        message: translateErrorMessage(`${EXPORT_FAIL}`, translations),
        type: TOAST_TYPE.ERROR
    });
}

function showCourseOperationsExportFailureToast(addToast, translations) {
    addToast({
        message: translations.REFRESH_YOUR_BROWSER,
        title: translations.ONLINK_EXPORT_FAILURE_TITLE,
        type: TOAST_TYPE.ERROR
    });
}

async function exportFleetReport(fleet, translations, addToast) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.EQUIPMENT_LIST, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const header = worksheet.addRow([
            translations.NAME,
            translations.TYPE,
            translations.AREA,
            translations.BRAND,
            translations.MODEL,
            translations.ONLINK_SERIAL_NUMBER,
            translations.STATUS,
            translations.HOURS
        ]);

        header.font = {
            bold: true
        };

        worksheet.getColumn(HOURS_COLUMN).style.numFmt = '#,##0.0';

        const rows = fleet.map((equipment) => [
            equipment.equipmentName,
            equipment.equipmentType,
            equipment.equipmentArea,
            equipment.manufacturerName,
            equipment.modelDescription,
            equipment.serialNumber,
            equipment.formattedStatus,
            accounting.parse(equipment.totalHours)
        ]);

        worksheet.addRows(rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Fleet-Export${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showExportFailureToast(addToast, translations);
    }
}

async function exportWorkboardReport(workboardReport, translations, addToast) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(
            translations.ONLINK_WORKBOARD_JOB_LIST,
            {
                views: [
                    {
                        state: 'frozen',
                        ySplit: 1
                    }
                ]
            }
        );

        const exportContent = getWorkboardJobListExportContent(workboardReport, translations);
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        worksheet.getColumn(EST_HRS_COLUMN).style.numFmt = '#,##0.0';
        worksheet.getColumn(ACT_HRS_COLUMN).style.numFmt = '#,##0.0';

        worksheet.addRows(exportContent.rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Workboard-Job-Export${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

function getDateFormatAndReportType(dateSelector, timeScale) {
    switch (timeScale) {
        case 'day':
            return {
                format: 'YYYY-MM-DD',
                reportType: 'DailyReport'
            };
        case 'year':
            return {
                format: 'YYYY',
                reportType: 'YearlyReport'
            };
        default:
            return {
                format: 'MMMM-YYYY',
                reportType: 'MonthlyReport'
            };
    }
}

function getExportDateInfo({
    dateSelector, endDate, startDate, timeScale
}) {
    if (startDate && endDate) {
        const start = moment(startDate, 'YYYYMMDD');
        const end = moment(endDate, 'YYYYMMDD');

        if (start.isSame(end, 'day')) {
            return {
                reportType: 'DailyReport',
                dateToken: start.format('YYYY-MM-DD')
            };
        }
        if (start.isSame(end, 'year') &&
            start.isSame(start.clone().startOf('year'), 'day') &&
            end.isSame(end.clone().endOf('year'), 'day')) {
            return {
                reportType: 'YearlyReport',
                dateToken: start.format('YYYY')
            };
        }
        if (start.isSame(end, 'month') &&
            start.isSame(start.clone().startOf('month'), 'day') &&
            end.isSame(end.clone().endOf('month'), 'day')) {
            return {
                reportType: 'MonthlyReport',
                dateToken: start.format('MMMM-YYYY')
            };
        }
        return {
            reportType: 'RangeReport',
            dateToken: `${start.format('YYYY-MM-DD')}_to_${end.format('YYYY-MM-DD')}`
        };
    }

    const {
        format, reportType
    } = getDateFormatAndReportType(dateSelector, timeScale);

    return {
        reportType,
        dateToken: moment(dateSelector).format(format)
    };
}

async function exportLaborWorkboardsReport({
    columnHeaderName,
    currencyPreference,
    dateSelector,
    endDate,
    formatNumbers,
    includeDate,
    startDate,
    timeScale,
    title,
    translations,
    workboardsReport,
    hasViewFinancialDataPermission,
    addToast
}) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.ONLINK_WORKBOARDS, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const exportContent = getLaborWorkboardsExportContent({
            columnHeaderName,
            currencyPreference,
            dateSelector,
            includeDate,
            timeScale,
            translations,
            workboardsReport,
            hasViewFinancialDataPermission
        });
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        const {
            reportType, dateToken
        } = getExportDateInfo({
            dateSelector,
            startDate,
            endDate,
            timeScale
        });

        worksheet.addRows(exportContent.rows);

        if (formatNumbers) {
            formatLaborReportWorksheetValues({
                worksheet,
                includeDate,
                currencyPreference,
                hasViewFinancialDataPermission
            });
        }

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `${title}-${reportType}-${dateToken}.xlsx`
        );
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportLaborByStatusDetailReport({
    dateSelect,
    endDate,
    formatNumbers,
    startDate,
    timeScale,
    translations,
    laborData,
    tertiarySelector,
    addToast
}) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.ONLINK_LABOR_REPORT, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const exportContent = getLaborByStatusExportContent({
            dateSelect,
            laborData,
            tertiarySelector,
            timeScale,
            translations
        });
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        worksheet.addRows(exportContent.rows);

        const {
            reportType, dateToken
        } = getExportDateInfo({
            dateSelector: dateSelect,
            startDate,
            endDate,
            timeScale
        });

        const footerRow = worksheet.addRow(exportContent.footer);

        footerRow.font = {
            bold: true
        };

        if (formatNumbers) {
            worksheet.getColumn(2).numFmt = '#,##0';
        }

        autofitWorksheet(worksheet);

        await downloadExcelFile(workbook, `${translations.ONLINK_LABOR_REPORT}-${reportType}-${dateToken}.xlsx`);
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportLaborByEmployeeDetailReport({
    dateSelect,
    endDate,
    formatNumbers,
    startDate,
    timeScale,
    translations,
    laborData,
    tertiarySelector,
    addToast
}) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.ONLINK_LABOR_REPORT, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const exportContent = getLaborByEmployeeExportContent({
            dateSelect,
            laborData,
            tertiarySelector,
            timeScale,
            translations
        });
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        worksheet.addRows(exportContent.rows);

        const {
            reportType, dateToken
        } = getExportDateInfo({
            dateSelector: dateSelect,
            startDate,
            endDate,
            timeScale
        });

        const footerRow = worksheet.addRow(exportContent.footer);

        footerRow.font = {
            bold: true
        };

        if (formatNumbers) {
            worksheet.getColumn(2).numFmt = '#,##0';
        }

        autofitWorksheet(worksheet);

        await downloadExcelFile(workbook, `${translations.ONLINK_LABOR_REPORT}-${reportType}-${dateToken}.xlsx`);
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportInventoryReport(partsData, translations, currencySymbol, addToast, includeUnitCostColumn) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.ONLINK_INVENTORY, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const header = worksheet.addRow([
            translations.TYPE,
            translations.NAME,
            translations.ONLINK_LOCATION,
            translations.MANUFACTURER,
            capitalizeFirstLetter(translations.PART_NUMBER),
            includeUnitCostColumn && `${translations.ONLINK_UNIT_COST} (${currencySymbol})`,
            `${translations.VALUE} (${currencySymbol})`,
            translations.ONLINK_STOCK,
            `${translations.ONLINK_IN}/${translations.ONLINK_OUT}`
        ].filter(Boolean));

        header.font = {
            bold: true
        };

        const rows = sortBy(partsData, [
            'partType',
            (partData) => partData?.name?.toLowerCase()
        ]).map((row) => [
            row.partType,
            row.name,
            row.binLocation,
            row.manufacturerName,
            row.partNumber,
            includeUnitCostColumn && row.formattedLastUnitCost,
            row.formattedTotalCost,
            row.formattedStock,
            row.formattedInOutValue
        ].filter(Boolean));

        worksheet.addRows(rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Inventory-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showExportFailureToast(addToast, translations);
    }
}

async function exportPartsHistoryReport(partsData, translations, addToast) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(`${translations.PARTS}-${translations.HISTORY}`, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const header = worksheet.addRow([
            translations.DATE,
            translations.NAME,
            capitalizeFirstLetter(translations.PART_NUMBER),
            translations.DETAILS,
            translations.ONLINK_PART_TYPE,
            translations.MANUFACTURER,
            translations.ONLINK_ADJUSTMENT
        ]);

        header.font = {
            bold: true
        };

        const rows = [...partsData]
            .sort((a, b) => {
                const dateCompareResult = dateCompare(b.date, a.date);

                if (dateCompareResult === 0) {
                    return alphaNumericCompare(a.name, b.name);
                }

                return dateCompareResult;
            })
            .map((row) => [
                row.date,
                row.name,
                row.partNumber,
                `${row.detailsType} ${row.detailsName}`,
                row.partType,
                row.manufacturer,
                parseInt(row.adjustment, 10)
            ]);

        worksheet.addRows(rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Parts-History-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showExportFailureToast(addToast, translations);
    }
}

async function exportEquipmentUptimeReport(
    rollupData,
    secondarySelector,
    translations,
    addToast
) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(
            translations.ONLINK_EQUIPMENT_UPTIME,
            {
                views: [
                    {
                        state: 'frozen',
                        ySplit: 1
                    }
                ]
            }
        );

        const exportContent = getEquipmentUptimeExportContent(
            rollupData,
            getHeaderColumnName(secondarySelector, translations),
            translations
        );
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        worksheet.addRows(exportContent.rows);

        if (exportContent.footer) {
            const totalRow = worksheet.addRow(exportContent.footer);

            totalRow.font = {
                bold: true
            };
        }

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Equipment-Uptime-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportLifetimeUptimeReport(
    lifetimeData,
    primarySelector,
    translations,
    addToast
) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(
            translations.ONLINK_LIFETIME_REPORT,
            {
                views: [
                    {
                        state: 'frozen',
                        ySplit: 1
                    }
                ]
            }
        );

        const exportContent = getLifetimeExportContent(lifetimeData, primarySelector, translations);
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true
        };

        worksheet.addRows(exportContent.rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Lifetime-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportEquipmentCostReport(rollupData, translations, addToast) {
    try {
        if (Object.keys(rollupData).length > 0) {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet(
                translations.ONLINK_EQUIPMENT_REPORT,
                {
                    views: [
                        {
                            state: 'frozen',
                            ySplit: 1
                        }
                    ]
                }
            );

            const exportContent = getEquipmentCostExportContent(rollupData, translations);
            const header = worksheet.addRow(exportContent.header);

            header.font = {
                bold: true,
                uppercase: true
            };

            worksheet.addRows(exportContent.rows);

            const totalRow = worksheet.addRow(exportContent.footer);

            totalRow.font = {
                bold: true
            };

            autofitWorksheet(worksheet);

            await downloadExcelFile(
                workbook,
                `Equipment-Cost-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
            );
        }
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

async function exportServiceHistoryReport(rollupData, translations, addToast) {
    try {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(translations.SERVICE_HISTORY, {
            views: [
                {
                    state: 'frozen',
                    ySplit: 1
                }
            ]
        });

        const exportContent = getServiceHistoryExportContent(rollupData, translations);
        const header = worksheet.addRow(exportContent.header);

        header.font = {
            bold: true,
            uppercase: true
        };

        worksheet.addRows(exportContent.rows);

        autofitWorksheet(worksheet);

        await downloadExcelFile(
            workbook,
            `Service-History-Export-${moment.utc().format('YYYY-MM-DD')}.xlsx`
        );
    } catch {
        showCourseOperationsExportFailureToast(addToast, translations);
    }
}

export {
    exportInventoryReport,
    exportFleetReport,
    exportWorkboardReport,
    exportLaborWorkboardsReport,
    exportPartsHistoryReport,
    exportEquipmentUptimeReport,
    exportLaborByStatusDetailReport,
    exportLifetimeUptimeReport,
    exportEquipmentCostReport,
    exportServiceHistoryReport,
    exportLaborByEmployeeDetailReport
};
